import { store, type AppState, type VictoryAchievement } from "../core/store";
import { socket } from "../core/socket";
import { showToast } from "../core/toast";
import type { PlayerDTO, VictoryVoteChoice } from "../../shared/types";
import { openGallery } from "./gallery";

/** First letter of up to the first two words of a name ("Mary Jane" -> "MJ"). */
function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean).slice(0, 2);
  return words.map((w) => Array.from(w)[0]!.toUpperCase()).join("") || "?";
}

interface AchievementCardCells {
  dots: HTMLElement;
  renderedDots: number;
}

export function mountVictory(container: HTMLElement): () => void {
  const wrap = document.createElement("div");
  wrap.className = "victory-wrap";

  const heading = document.createElement("h2");
  heading.textContent = "Victory Ceremony";

  const achHeading = document.createElement("h3");
  achHeading.textContent = "Achievements";
  achHeading.classList.add("hidden");
  const achList = document.createElement("div");
  achList.className = "achievement-list";

  const revealHeading = document.createElement("h3");
  revealHeading.textContent = "Final Standings";
  revealHeading.classList.add("hidden");
  const revealList = document.createElement("div");
  revealList.className = "reveal-list";

  const waiting = document.createElement("p");
  waiting.className = "hint-text";
  waiting.textContent = "Tallying the results...";

  const actions = document.createElement("div");
  actions.className = "victory-actions hidden";

  wrap.append(heading, achHeading, achList, revealHeading, revealList, waiting, actions);
  container.appendChild(wrap);

  let renderedAchievements = 0;
  let achievementCards: AchievementCardCells[] = [];
  let renderedReveals = 0;
  let scaffoldedPlayerCount = -1;
  let revealRowCells = new Map<number, { row: HTMLElement; rank: HTMLElement; name: HTMLElement; score: HTMLElement }>();
  let myVote: VictoryVoteChoice | null = null;
  let actionsBuiltFor: "host" | "player" | null = null;
  let wasVictoryFinished = false;

  // `container` (#main-area) is the scrollable element itself, not some inner
  // wrapper - scrolling IT to its own bottom, rather than `scrollIntoView`-ing
  // whatever the latest individual card/row happens to be, is what keeps
  // *everything* newly revealed in view as the ceremony progresses (an
  // achievement card, a reveal row filling in, or the action buttons finally
  // appearing) - not just the specific element the previous update happened to
  // touch. Deferred one frame so a just-appended card or a just-unhidden
  // section has actually been laid out (and counted into scrollHeight) before
  // we measure it.
  function scrollToBottom(): void {
    requestAnimationFrame(() => {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    });
  }

  // A name tooltip that follows the mouse while over an earner's dot. One shared
  // element for the whole view, parked on <body> so no scroll container or
  // overflow clipping can cut it off.
  const dotTooltip = document.createElement("div");
  dotTooltip.className = "achievement-dot-tooltip hidden";
  document.body.appendChild(dotTooltip);

  function moveDotTooltip(e: MouseEvent): void {
    dotTooltip.style.left = `${e.clientX + 14}px`;
    dotTooltip.style.top = `${e.clientY + 16}px`;
  }

  achList.addEventListener("mouseover", (e) => {
    const dot = (e.target as HTMLElement).closest<HTMLElement>(".achievement-dot");
    if (!dot) return;
    dotTooltip.textContent = dot.dataset.name ?? "";
    dotTooltip.classList.remove("hidden");
    moveDotTooltip(e);
  });
  achList.addEventListener("mousemove", (e) => {
    if (!dotTooltip.classList.contains("hidden")) moveDotTooltip(e);
  });
  achList.addEventListener("mouseout", (e) => {
    const dot = (e.target as HTMLElement).closest(".achievement-dot");
    if (dot) dotTooltip.classList.add("hidden");
  });

  function buildAchievementCard(achievement: VictoryAchievement): AchievementCardCells {
    const card = document.createElement("div");
    card.className = `achievement-card achievement-card--${achievement.tier}`;
    // The color swipe in the card's background (see .achievement-card::before)
    // runs for exactly as long as the wait before the earners' dots appear.
    const waitSeconds = store.getState().config?.timers.achievementPlayerRevealDelaySeconds ?? 4;
    card.style.setProperty("--reveal-duration", `${waitSeconds}s`);

    const title = document.createElement("div");
    title.className = "achievement-title";
    title.textContent = `${achievement.title} - +${achievement.points} points`;

    const desc = document.createElement("div");
    desc.className = "achievement-desc";
    desc.textContent = achievement.description;

    // Reserved up front so the card keeps its height when the dots arrive,
    // instead of everything below it jumping down.
    const dots = document.createElement("div");
    dots.className = "achievement-dots";

    card.append(title, desc, dots);
    achList.appendChild(card);
    return { dots, renderedDots: 0 };
  }

  function appendDot(dots: HTMLElement, playerId: string, players: PlayerDTO[]): void {
    const player = players.find((p) => p.id === playerId);
    const name = player?.name ?? "Someone";
    const dot = document.createElement("span");
    dot.className = "achievement-dot";
    dot.dataset.name = name;
    dot.setAttribute("aria-label", name);
    dot.style.backgroundColor = player?.color ?? "var(--warning)";
    dot.textContent = initialsOf(name);
    dots.appendChild(dot);
  }

  function ensureRevealScaffold(activePlayerCount: number): void {
    if (scaffoldedPlayerCount === activePlayerCount) return;
    scaffoldedPlayerCount = activePlayerCount;
    revealList.innerHTML = "";
    revealRowCells = new Map();
    if (activePlayerCount === 0) return;
    revealHeading.classList.remove("hidden");
    // All N rows render immediately, ranked 1 (best, top) through N (worst,
    // bottom) - each starts as a placeholder and gets filled in place as its
    // victory:reveal arrives, rather than the list growing one row at a time.
    for (let rank = 1; rank <= activePlayerCount; rank++) {
      const row = document.createElement("div");
      row.className = "reveal-row reveal-row--pending";
      const rankEl = document.createElement("span");
      rankEl.className = "reveal-rank";
      rankEl.textContent = `#${rank}`;
      const nameEl = document.createElement("span");
      nameEl.className = "reveal-name";
      nameEl.textContent = "???";
      const scoreEl = document.createElement("span");
      scoreEl.className = "reveal-score";
      scoreEl.textContent = "?";
      row.append(rankEl, nameEl, scoreEl);
      revealList.appendChild(row);
      revealRowCells.set(rank, { row, rank: rankEl, name: nameEl, score: scoreEl });
    }
  }

  function buildVoteButton(choice: VictoryVoteChoice, label: string, count: number): HTMLElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "vote-button";
    const voted = myVote === choice;
    btn.classList.toggle("vote-button--voted", voted);
    btn.textContent = `${label} [${count > 0 ? "+" + count : "+1"}]`;
    btn.addEventListener("click", () => {
      myVote = myVote === choice ? null : choice;
      socket.emit("victory:vote", choice);
      render();
    });
    return btn;
  }

  function buildHostActionButton(
    label: string,
    count: number,
    emit: () => void,
    variant: "primary-button" | "secondary-button"
  ): HTMLElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = variant;
    btn.textContent = `${label} (+${count})`;
    btn.addEventListener("click", () => {
      btn.disabled = true;
      showToast(`${label}...`, "success");
      emit();
    });
    return btn;
  }

  function renderActions(): void {
    const state = store.getState();
    actions.classList.toggle("hidden", !state.victoryFinished);
    if (!state.victoryFinished) {
      actionsBuiltFor = null;
      return;
    }

    const role = state.isHost ? "host" : "player";
    // Vote counts change often (every click, from anyone) - rebuilding the
    // host's action buttons from scratch on every single tally update is fine
    // (they're not text inputs), but skip rebuilding when only the visible
    // role hasn't changed, to avoid unnecessary DOM churn while idle.
    if (actionsBuiltFor === role && actions.childElementCount > 0 && role === "host") {
      const playAgainBtn = actions.querySelector<HTMLButtonElement>("[data-vote-action='playAgain']");
      const lobbyBtn = actions.querySelector<HTMLButtonElement>("[data-vote-action='returnToLobby']");
      if (playAgainBtn) playAgainBtn.textContent = `Play Again (+${state.victoryVotes.playAgain})`;
      if (lobbyBtn) lobbyBtn.textContent = `Return to Lobby (+${state.victoryVotes.returnToLobby})`;
      return;
    }

    actions.innerHTML = "";
    actionsBuiltFor = role;

    // Above the Play Again / Return to Lobby buttons, for everyone.
    const galleryBtn = document.createElement("button");
    galleryBtn.type = "button";
    galleryBtn.className = "secondary-button";
    galleryBtn.textContent = "Show Gallery";
    galleryBtn.addEventListener("click", openGallery);
    actions.appendChild(galleryBtn);

    if (state.isHost) {
      const playAgainBtn = buildHostActionButton(
        "Play Again",
        state.victoryVotes.playAgain,
        () => socket.emit("host:playAgain"),
        "primary-button"
      );
      playAgainBtn.dataset.voteAction = "playAgain";
      const lobbyBtn = buildHostActionButton(
        "Return to Lobby",
        state.victoryVotes.returnToLobby,
        () => socket.emit("host:returnToWaitingRoom"),
        "secondary-button"
      );
      lobbyBtn.dataset.voteAction = "returnToLobby";
      const hostRow = document.createElement("div");
      hostRow.className = "vote-row";
      hostRow.append(playAgainBtn, lobbyBtn);
      actions.append(hostRow);
    } else {
      const voteRow = document.createElement("div");
      voteRow.className = "vote-row";
      voteRow.append(
        buildVoteButton("playAgain", "Play again", state.victoryVotes.playAgain),
        buildVoteButton("returnToLobby", "Return to Lobby", state.victoryVotes.returnToLobby)
      );
      const hostHint = document.createElement("p");
      hostHint.className = "hint-text";
      hostHint.textContent = "Waiting for the host...";
      actions.append(voteRow, hostHint);
    }
  }

  // A brand-new ceremony (server's "victory:started" resets the store's
  // victoryAchievements/victoryReveals back to []) is detected here rather
  // than relying solely on mainArea.ts unmounting this view between games -
  // VICTORY_CEREMONY and RETURN_TO_WAITING_ROOM share one ViewKey ("victory"),
  // so going Victory -> Return to Lobby -> Victory again (or any path that
  // re-enters VICTORY_CEREMONY without this component ever unmounting) would
  // otherwise leave the previous game's achievement cards/reveal rows sitting
  // in the DOM, with rendered counts that never go back down to let a fresh
  // render loop repopulate them. Length going DOWN only ever happens on such
  // a reset (both arrays are otherwise append-only within one ceremony).
  function resetForNewCeremonyIfNeeded(state: AppState): void {
    if (state.victoryAchievements.length >= renderedAchievements && state.victoryReveals.length >= renderedReveals) {
      return;
    }
    achList.innerHTML = "";
    achievementCards = [];
    dotTooltip.classList.add("hidden");
    revealList.innerHTML = "";
    achHeading.classList.add("hidden");
    revealHeading.classList.add("hidden");
    waiting.classList.remove("hidden");
    renderedAchievements = 0;
    renderedReveals = 0;
    scaffoldedPlayerCount = -1;
    revealRowCells = new Map();
    actionsBuiltFor = null;
    wasVictoryFinished = false;
  }

  function render(): void {
    const state = store.getState();
    resetForNewCeremonyIfNeeded(state);

    const players = state.snapshot?.players ?? [];
    let achievementsChanged = false;
    if (state.victoryAchievements.length !== renderedAchievements) {
      achHeading.classList.toggle("hidden", state.victoryAchievements.length === 0);
      for (let i = renderedAchievements; i < state.victoryAchievements.length; i++) {
        achievementCards.push(buildAchievementCard(state.victoryAchievements[i]!));
      }
      renderedAchievements = state.victoryAchievements.length;
      achievementsChanged = true;
    }
    // Earners arrive one at a time after their achievement is already on screen.
    state.victoryAchievements.forEach((achievement, i) => {
      const cells = achievementCards[i];
      if (!cells) return;
      for (let d = cells.renderedDots; d < achievement.playerIds.length; d++) {
        appendDot(cells.dots, achievement.playerIds[d]!, players);
        achievementsChanged = true;
      }
      cells.renderedDots = achievement.playerIds.length;
    });
    if (achievementsChanged) scrollToBottom();

    const activePlayerCount = (state.snapshot?.players ?? []).filter((p) => !p.isSpectator).length;
    ensureRevealScaffold(activePlayerCount);

    if (state.victoryReveals.length !== renderedReveals) {
      for (let i = renderedReveals; i < state.victoryReveals.length; i++) {
        const entry = state.victoryReveals[i]!;
        const player = state.snapshot?.players.find((p) => p.id === entry.playerId);
        const cells = revealRowCells.get(entry.rank);
        if (!cells) continue;
        cells.row.classList.remove("reveal-row--pending");
        if (entry.place === 1) cells.row.classList.add("reveal-winner");
        cells.rank.textContent = `#${entry.place}`;
        cells.name.textContent = player?.name ?? "Someone";
        cells.name.style.color = player?.color ?? "#fff";
        cells.score.textContent = String(entry.score);
      }
      renderedReveals = state.victoryReveals.length;
      scrollToBottom();
    }

    waiting.classList.toggle("hidden", state.victoryAchievements.length > 0 || scaffoldedPlayerCount > 0);
    renderActions();

    // The action buttons (Play Again / Return to Lobby) only appear once
    // victory:finished lands, right after the last reveal row - without this,
    // that transition alone (no achievement/reveal count change alongside it)
    // would leave the newly-revealed buttons unscrolled-to.
    if (state.victoryFinished && !wasVictoryFinished) {
      scrollToBottom();
    }
    wasVictoryFinished = state.victoryFinished;
  }

  render();
  const unsub = store.subscribe(render);
  return () => {
    unsub();
    dotTooltip.remove();
  };
}
